import {Observable, of, OperatorFunction, pipe, scan, switchMap, throwError, timer} from 'rxjs';
import {catchError, debounceTime, filter, map, startWith, takeWhile} from 'rxjs/operators';
import {AbstractControl, FormControl} from '@angular/forms';

/**
 * Emits a per-second countdown from `seconds` down to 0 inclusive, then completes.
 * Example: countdown(3) emits 3, 2, 1, 0.
 */
export const countdown = (seconds: number): Observable<number> =>
    timer(0, 1000).pipe(
        map(elapsed => seconds - elapsed),
        takeWhile(remaining => remaining >= 0),
    );

export const nullableSwitchMap = <T, R>(
    fn: (value: NonNullable<T>) => Observable<R>,
): OperatorFunction<T, R | null> =>
    pipe(switchMap(value => (value != null ? fn(value) : of(null))));

export const filterNulls = <T>(): OperatorFunction<T | null, T> =>
    pipe(filter((it): it is T => it !== null));

export const filterNullishRx = <T>(): OperatorFunction<T | null | undefined, T> =>
    pipe(filter((it): it is T => it != null));

export const nullableMap = <T, R>(
    fn: (value: NonNullable<T>) => R,
): OperatorFunction<T, R | null> => pipe(map(value => (value != null ? fn(value) : null)));

export const fromControl = <T>(
    control: AbstractControl<T> | FormControl<T>,
    value?: T,
): Observable<T> =>
    control.valueChanges.pipe(
        filter(() => control.valid),
        startWith(value ?? control.value),
    );

export const fromControlWithDebounceTime = <T>(
    control: AbstractControl<T> | FormControl<T>,
    value?: T,
): Observable<T> =>
    control.valueChanges.pipe(
        debounceTime(200),
        filter(() => control.valid),
        startWith(value ?? control.value),
    );

export const mapError = <R>(fn: (value: any) => R): OperatorFunction<any, R | null> =>
    pipe(catchError(err => throwError(() => fn(err))));

export type Loaded<T> = {loading: false; value: T};
export type HiddenValueWhileLoading<T> = {loading: true} | Loaded<T>;
export type ExposedValueWhileLoading<T> = {loading: boolean; value: T};

export function switchMapWithLoading<R, T>(
    observableFunction: (value: R) => Observable<T>,
): OperatorFunction<R, HiddenValueWhileLoading<T>> {
    return (source: Observable<R>) =>
        source.pipe(
            switchMap(value =>
                observableFunction(value).pipe(
                    map(data => ({value: data, loading: false})),
                    startWith({loading: true as const}),
                ),
            ),
        );
}

export function switchMapWithLoadingExposedValue<R, T>(
    observableFunction: (value: R) => Observable<T>,
    initialValue: T,
): OperatorFunction<R, ExposedValueWhileLoading<T>> {
    return (source: Observable<R>) =>
        source.pipe(
            switchMap(value =>
                observableFunction(value).pipe(
                    map(data => ({value: data, loading: false})),
                    startWith({value: initialValue, loading: true}),
                ),
            ),
            scan((previous, current) => ({
                loading: current.loading,
                value: current.loading ? previous.value : current.value,
            })),
        );
}
